import { Add } from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../../../common/components/CustomDialog";
import CustomLoader from "../../../../../common/components/CustomLoader";
import CustomSnackbar from "../../../../../common/components/CustomSnackbar";
import CustomTable from "../../../../../common/components/CustomTable";
import { getForm } from "../../../../../common/config/forms";
import { beehivesAlertsConfigHeadcells } from "../../../../../common/config/headcells";
import { useAuth } from "../../../../../common/hooks/useAuth";
import { useLoading } from "../../../../../common/hooks/useLoading";
import { IBeehive } from "../../../../../common/interfaces/beehive";
import { IBeehiveAlertsConfiguration } from "../../../../../common/interfaces/beehiveAlertsConfiguration";
import { ICustomDialogProps } from "../../../../../common/interfaces/dialog";
import { ICustomPaginationProps } from "../../../../../common/interfaces/pagination";
import { ACTIONS } from "../../../../../common/interfaces/table";
import beehiveAlertsConfigurationService from "../../../../../common/services/beehiveAlertsConfiguration";

interface IAlertsTabProps {
    beehiveId: string | undefined;
    currentBeehive: IBeehive | undefined;
    parentDialogOpen: boolean;
}

const AlertsTab: FC<IAlertsTabProps> = (props) => {
    const { beehiveId, parentDialogOpen, currentBeehive } = props;

    // global
    const { permissionsState, permissionsData, loginData } = useAuth();
    const { loading } = useLoading();
    const { t } = useTranslation();
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.up("sm"))

    const [action, setAction] = useState<ACTIONS>();
    const [currentBeehiveAlerts, setCurrentBeehiveAlerts] = useState<(IBeehiveAlertsConfiguration & { actions: { type: ACTIONS, message: string }[] })[]>([]);
    const [currentBeehiveAlertsConfiguration, setCurrentBeehiveAlertsConfiguration] = useState<IBeehiveAlertsConfiguration | undefined>(undefined);

    // form
    const addAlertConfigurationForm = getForm<IBeehiveAlertsConfiguration>("addAlertConfiguration");

    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<React.ReactNode>(<></>);

    const setActionsWithPermissions = () => {
        let tempActions: ({ type: ACTIONS, message: string })[] = []
        if (permissionsState && permissionsState.existEditAlertsPermission)
            tempActions.push({
                type: ACTIONS.EDIT,
                message: t("beehiveDetailsPage.tabs.editAlertButtonLabel")
            })
        if (permissionsState && permissionsState.existDeleteAlertsPermission)
            tempActions.push({
                type: ACTIONS.DELETE,
                message: t("beehiveDetailsPage.tabs.deleteAlertButtonLabel")
            })
        tempActions = tempActions.filter(action => action !== undefined)
        return tempActions
    }

    const getCurrentBeehiveAlerts = async (id: string) => {
        try {
            const response = await beehiveAlertsConfigurationService.getAll(id, page * rowsPerPage, rowsPerPage)
            setCurrentBeehiveAlerts(response.content.map(alert => {
                return {
                    ...alert,
                    field: t(`beehiveDetailsPage.tabs.alertsForm.${alert.field}`),
                    actions: setActionsWithPermissions()
                }
            }))
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        if (beehiveId) {
            getCurrentBeehiveAlerts(beehiveId)
        }
    }, [beehiveId, rowsPerPage, page])

    // page change handler (pagination)
    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    // num. of rows change handler (pagination)
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value));
    };

    // action handler (icon buttons from the table)
    const onActionClicked = (label: ACTIONS, data: (IBeehiveAlertsConfiguration & { actions: { type: ACTIONS, message: string }[] }) | undefined) => {
        setAction(label);
        if (data) {
            const { actions, ...alertData } = data
            setCurrentBeehiveAlertsConfiguration(alertData);
        }
        switch (label) {
            case "add": {
                setDialogTitle(t("beehiveDetailsPage.tabs.newAlertButtonLabel"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "edit": {
                if (data) {
                    setDialogTitle(t("beehiveDetailsPage.tabs.editAlertButtonLabel"));
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            case "delete": {
                setDialogTitle(t("beehiveDetailsPage.tabs.deleteAlertButtonLabel"));
                setDialogBody(
                    <Typography variant="body1">
                        {t("beehiveDetailsPage.tabs.deleteStatDialogWarningText")}
                    </Typography>
                );
                setOpenDialog(true);
                break;
            }
            default: {
                break;
            }
        };
    }

    // close dialog handler
    const handleClose = () => {
        setOpenDialog(false);
    };

    // api request handler
    const apiRequest = (data: IBeehiveAlertsConfiguration | undefined) => {
        if (data && beehiveId) {
            if (action === "add")
                return beehiveAlertsConfigurationService.post(beehiveId, data)
            else if (action === "edit" && currentBeehiveAlertsConfiguration)
                return beehiveAlertsConfigurationService.put(beehiveId, currentBeehiveAlertsConfiguration.id, data)
        } else if (action === "delete" && beehiveId && currentBeehiveAlertsConfiguration)
            return beehiveAlertsConfigurationService.delete(beehiveId, currentBeehiveAlertsConfiguration.id)
        else {
            return Promise.reject()
        }
    }

    // confirm dialog handler
    const handleActionConfirm = async (data: IBeehiveAlertsConfiguration | undefined) => {
        handleClose();
        if (beehiveId) {
            try {
                await apiRequest(data);
                setOpenSnackbar(true);
                setResponseType("success");
                setMessageSnackbar(t("genericSuccess"));
                getCurrentBeehiveAlerts(beehiveId);
            } catch (error: unknown) {
                setOpenSnackbar(true);
                setResponseType("error");
                if (error instanceof AxiosError) {
                    setMessageSnackbar(error.response?.data.content.message)
                } else {
                    setMessageSnackbar(t("genericError"));
                }
            }
        }
    }

    // pagination props
    const paginationProps: ICustomPaginationProps = {
        type: "table",
        paginationProps: {
            count: currentBeehiveAlerts.length,
            page,
            onPageChange: handleChangePage,
            rowsPerPage,
            onRowsPerPageChange: handleChangeRowsPerPage,
        },
    };

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: action === "delete" ? "confirm" : "form",
        close: handleClose,
        confirm: () => handleActionConfirm(undefined),
        title: dialogTitle,
        open: openDialog,
        confirmButtonLabel: action === "delete" ? t("beehiveDetailsPage.tabs.alertsForm.confirmButton") : t("beehiveDetailsPage.tabs.alertsForm.saveChangesButton"),
        closeButtonLabel: t("beehiveDetailsPage.tabs.alertsForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...addAlertConfigurationForm,
            initialValues: action === "edit" ? { ...currentBeehiveAlertsConfiguration } : {
                field: "",
                minValue: "",
                maxValue: ""
            },
            submit: (data) => handleActionConfirm(data),
            isDialog: true,
            customStyles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }
        }
    };

    const getCreatePermissions = () => {
        return permissionsState &&
            permissionsState.existViewAlertsPermission &&
            permissionsData &&
            (
                permissionsData['bhalerts'].includes('bhalerts_create_all') ||
                (
                    permissionsData['bhalerts'].includes("bhalerts_create_only_area") &&
                    loginData &&
                    currentBeehive &&
                    loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                    permissionsData['bhalerts'].includes('bhalerts_create_only_owned') &&
                    loginData &&
                    currentBeehive &&
                    currentBeehive.ownerId === loginData.content.user.id
                )
            )
    }

    const getEditPermissions = () => {
        return permissionsState &&
            permissionsState.existViewAlertsPermission &&
            permissionsData &&
            (
                permissionsData['bhalerts'].includes('bhalerts_edit_all') ||
                (
                    permissionsData['bhalerts'].includes("bhalerts_edit_only_area") &&
                    loginData &&
                    currentBeehive &&
                    !!loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                    permissionsData['bhalerts'].includes('bhalerts_edit_only_owned') &&
                    loginData &&
                    currentBeehive &&
                    currentBeehive.ownerId === loginData.content.user.id
                )
            )
    }

    const getDeletePermissions = () => {
        return permissionsState &&
            permissionsState.existViewAlertsPermission &&
            permissionsData &&
            (
                permissionsData['bhalerts'].includes('bhalerts_delete_all') ||
                (
                    permissionsData['bhalerts'].includes("bhalerts_delete_only_area") &&
                    loginData &&
                    currentBeehive &&
                    loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                    permissionsData['bhalerts'].includes('bhalerts_delete_only_owned') &&
                    loginData &&
                    currentBeehive &&
                    currentBeehive.ownerId === loginData.content.user.id
                )
            )
    }


    return <Box sx={{ py: 2 }}>
        <CustomDialog {...dialogProps}>
            {dialogBody}
        </CustomDialog>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        {
            loading && !openDialog && !parentDialogOpen ? <CustomLoader /> :
                <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t("beehiveDetailsPage.tabs.alertsTabTitle")}
                        </Typography>
                        {
                            getCreatePermissions() &&
                            <Button variant="outlined"
                                sx={(theme) => ({
                                    [theme.breakpoints.only("xs")]: {
                                        minWidth: { xs: 0 },
                                        width: { xs: 34 },
                                        height: { xs: 32 },
                                        padding: { xs: 2 },
                                        ml: 1
                                    },
                                })}
                                onClick={() => onActionClicked(ACTIONS.ADD, undefined)}
                            >
                                <Add sx={{ mr: !smallScreen ? 0 : 0.5 }} />
                                {smallScreen && t("beehiveDetailsPage.tabs.newAlertButtonLabel")}
                            </Button>
                        }
                    </Box>
                    <CustomTable
                        headcells={
                            permissionsState && permissionsState.existAlertsPermissions
                                ?
                                beehivesAlertsConfigHeadcells
                                :
                                [...beehivesAlertsConfigHeadcells.filter(headcell => headcell.label !== "action")]
                        }
                        rows={currentBeehiveAlerts}
                        onIconActionClicked={onActionClicked}
                        pagination={paginationProps}
                        isEditPermitted={!!getEditPermissions()}
                        isDeletePermitted={!!getDeletePermissions()}
                    />
                </Box>
        }
    </Box>
}

export default AlertsTab;